import DP from "dompurify";
import { Marked, RendererObject, Token, Tokens } from "marked";
import { logger } from "shared/utils/logger";

/** @private */
function _noimpl(input: unknown) {
    logger.warn("md plainTextRenderer found unsuported node: " + JSON.stringify(input));
    return "";
}

type ListItemParser = { parse: (tokens: Token[]) => string };

/** @private */
function _renderListItem(item: Tokens.ListItem, index: number, ordered: boolean, parser: ListItemParser) {
    const prefix = ordered ? `${index + 1}. ` : "* ";
    // Separate inline content from nested block content (like nested lists)
    const inlineTokens = item.tokens.filter((t) => t.type !== "list");
    const nestedLists = item.tokens.filter((t) => t.type === "list");

    const inlineContent = parser.parse(inlineTokens).trimEnd();
    const nestedContent = nestedLists
        .map((nestedList) => {
            const nested = parser.parse([nestedList]).trimEnd();
            return nested // indent
                .split("\n")
                .map((line) => " ".repeat(prefix.length) + line)
                .join("\n");
        })
        .join("\n");

    return nestedContent ? `${prefix}${inlineContent}\n${nestedContent}` : `${prefix}${inlineContent}`;
}

const plainTextRenderer: RendererObject = {
    paragraph({ tokens }) {
        return this.parser.parseInline(tokens);
    },
    blockquote({ tokens }) {
        return this.parser.parseInline(tokens);
    },
    br() {
        return "\n";
    },
    checkbox({ checked }) {
        return checked ? "[x]" : "[ ]";
    },
    heading({ tokens }) {
        return this.parser.parseInline(tokens) + "\n";
    },
    em({ tokens }) {
        return this.parser.parseInline(tokens);
    },
    del({ tokens }) {
        return this.parser.parseInline(tokens);
    },
    hr() {
        return "---\n";
    },
    link({ href, text }) {
        if (!text) return href;
        return `${text} (${href})`;
    },
    list({ items, ordered }) {
        return items.map((item, i) => _renderListItem(item, i, ordered, this.parser)).join("\n") + "\n";
    },
    listitem({ tokens }) {
        return this.parser.parse(tokens);
    },
    strong({ tokens }) {
        return this.parser.parseInline(tokens);
    },
    text({ text }) {
        return text;
    },
    space() {
        return "\n";
    },
    table: _noimpl,
    tablecell: _noimpl,
    code: _noimpl,
    codespan: _noimpl,
    image: _noimpl,
    html: _noimpl,
};

const plainTextMarked = new Marked({ renderer: plainTextRenderer });

const htmlRenderer: RendererObject = {
    checkbox({ checked }) {
        return `<input type="checkbox" ${checked && "checked"} disabled>`;
    },
    listitem({ tokens, loose, task, checked }) {
        const content = this.parser.parse(tokens, loose);

        if (task) {
            const checkbox = this.checkbox({ checked: !!checked });
            return `<li><label>${checkbox}${content}</label></li>`;
        }

        return `<li>${content}</li>`;
    },
};

const htmlMarked = new Marked({ renderer: htmlRenderer });

export async function mdToTxt(md: string): Promise<string> {
    const txt = await Promise.resolve(plainTextMarked.parse(md));

    // strip any trailing tags that aren't processed by marked such as `<u>`. See
    // https://github.com/markedjs/marked/issues/1693
    // https://github.com/markedjs/marked/pull/1872
    return DP.sanitize(txt, { ALLOWED_TAGS: ["#text"] });
}

export async function mdToHtml(md: string) {
    const dirty = await Promise.resolve(htmlMarked.parse(md));
    return DP.sanitize(dirty);
}
